import type { ReactNode } from 'react'

/**
 * Banner — full-width page-level alert strip.
 *
 * Used at the top of page content (below PageHead) for escalation and
 * conflict signals. Four kinds:
 *
 *   escalation — info bg, for items forwarded to Director/HOD
 *   conflict   — danger bg, for hard date/coverage conflicts
 *   warning    — warning bg, for soft warnings (blackout window, policy note)
 *   info       — neutral (surface), for general informational context
 *
 * Register discipline: Banner is a content primitive, not chrome. It lives
 * INSIDE the page content area, never in the sidebar or topbar.
 */

export type BannerKind = 'escalation' | 'conflict' | 'info' | 'warning'

export interface BannerProps {
  kind: BannerKind
  title: string
  subtitle?: string
  action?: ReactNode
}

interface KindStyle {
  background: string
  border: string
  iconColor: string
  glyph: string
}

const KIND_STYLES: Record<BannerKind, KindStyle> = {
  escalation: {
    background: 'var(--info-bg)',
    border: '1px solid var(--info-line)',
    iconColor: 'var(--info-fg)',
    glyph: '↑',
  },
  conflict: {
    background: 'var(--danger-bg)',
    border: '1px solid var(--danger-line)',
    iconColor: 'var(--danger-fg)',
    glyph: '⚠',
  },
  warning: {
    background: 'var(--warning-bg)',
    border: '1px solid var(--warning-line)',
    iconColor: 'var(--warning-fg)',
    glyph: '△',
  },
  info: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    iconColor: 'var(--text-2)',
    glyph: 'ℹ',
  },
}

export function Banner({ kind, title, subtitle, action }: BannerProps): JSX.Element {
  const styles = KIND_STYLES[kind]

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="banner"
      data-kind={kind}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        padding: '12px 16px',
        background: styles.background,
        border: styles.border,
        borderRadius: 'var(--r-md)',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
        <span
          aria-hidden
          style={{
            fontSize: 14,
            lineHeight: 1,
            color: styles.iconColor,
            marginTop: 1,
            flexShrink: 0,
          }}
        >
          {styles.glyph}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text)',
              lineHeight: 1.4,
            }}
          >
            {title}
          </div>
          {subtitle ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--text-2)',
                lineHeight: 1.5,
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
      {action ? (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{action}</div>
      ) : null}
    </div>
  )
}
